import { useQuery } from "@tanstack/react-query";

import type {
    MeditationOpening,
    MeditationPauseMultiplier,
    MeditationType,
} from "~/features/meditations/types";

interface CacheStatusResponse {
    cached: boolean;
    storagePath: string | null;
}

interface UseMeditationCacheStatusArgs {
    type: MeditationType;
    voice: string;
    pauseMultiplier?: MeditationPauseMultiplier;
    opening?: MeditationOpening;
    sessionId?: string;
    partInteractionId?: string;
    enabled?: boolean;
}

// AIDEV-NOTE: Dev-only probe of /api/meditation/cache-status — tells us whether
// clicking Play will replay a cached MP3 or kick off a fresh generation.
// Guarded at the call site by `ENV_NAME !== "production"`; we do not short-circuit
// here so Playwright/staging can still exercise the path.
export function useMeditationCacheStatus({
    type,
    voice,
    pauseMultiplier = 1,
    opening = "full",
    sessionId,
    partInteractionId,
    enabled = true,
}: UseMeditationCacheStatusArgs) {
    return useQuery<CacheStatusResponse>({
        queryKey: [
            "meditation-cache-status",
            type,
            voice,
            pauseMultiplier,
            opening,
            sessionId ?? null,
            partInteractionId ?? null,
        ],
        enabled,
        staleTime: 30_000,
        retry: false,
        queryFn: async () => {
            const url = new URL(
                "/api/meditation/cache-status",
                window.location.origin,
            );
            url.searchParams.set("type", type);
            url.searchParams.set("voice", voice);
            if (pauseMultiplier > 1) {
                url.searchParams.set(
                    "pauseMultiplier",
                    String(pauseMultiplier),
                );
            }
            if (opening === "brief") {
                url.searchParams.set("opening", opening);
            }
            if (sessionId) url.searchParams.set("sessionId", sessionId);
            if (partInteractionId)
                url.searchParams.set("partInteractionId", partInteractionId);

            const response = await fetch(url.toString(), {
                credentials: "include",
            });
            if (!response.ok) {
                throw new Error(
                    `cache-status probe failed: ${response.status}`,
                );
            }
            return (await response.json()) as CacheStatusResponse;
        },
    });
}
